<template>
  <div class="messenger-chat-message-attachments">
    <messenger-chat-message-attachment-wrapper
      v-for="(attachment, index) in attachments"
      :key="attachment.id"
      :download-url="attachment.downloadUrl"
    >
      <widget-attachment-regular-layout
        v-if="layoutType === 'regular'"
        :mime-type="attachment.mimeType"
        :name="attachment.name"
        :size="attachment.size"
        :preview-url="attachment.previewUrl"
      >
        <template v-slot:message-info-compensator>
          <slot name="message-info-compensator" />
        </template>
      </widget-attachment-regular-layout>

      <widget-attachment-preview-layout
        v-else-if="layoutType === 'preview'"
        :name="attachment.name"
        :size="attachment.size"
        :type="isImageMime(attachment.mimeType) ? 'image' : 'video'"
        :url="attachment.downloadUrl || ''"
        :width="isPreviewMetadata(attachment.metadata) ? attachment.metadata?.width : undefined"
        :height="isPreviewMetadata(attachment.metadata) ? attachment.metadata?.height : undefined"
      >
        <template v-slot:message-info-compensator>
          <slot name="message-info-compensator" />
        </template>
      </widget-attachment-preview-layout>

      <widget-attachment-one-line-layout
        v-else-if="layoutType === 'one-line'"
        :mime-type="attachment.mimeType"
        :name="attachment.name"
        :size="attachment.size"
      >
        <template
          v-if="index === attachments.length - 1"
          v-slot:message-info-compensator
        >
          <slot name="message-info-compensator" />
        </template>
      </widget-attachment-one-line-layout>
    </messenger-chat-message-attachment-wrapper>
  </div>
</template>

<script lang="ts" setup>
import type { WidgetUploadedAttachment } from '@widgets/shared/types'
import MessengerChatMessageAttachmentWrapper from '@widgets/messenger/components/messages/MessengerChatMessageAttachmentWrapper.vue'
import WidgetAttachmentRegularLayout from '@widgets/shared/components/attachment/WidgetAttachmentRegularLayout.vue'
import WidgetAttachmentPreviewLayout from '@widgets/shared/components/attachment/WidgetAttachmentPreviewLayout.vue'
import WidgetAttachmentOneLineLayout from '@widgets/shared/components/attachment/WidgetAttachmentOneLineLayout.vue'
import {
  isMessengerApiAttachmentImageMetadata,
  isMessengerApiAttachmentVideoMetadata,
  type MessengerApiAttachmentMetadata,
} from '@widgets/messenger/types/api/common'
import { computed } from '@/composition/vue/compositionApi'
import { isImageMime, isVideoMime } from '@/utils/file/mime'

const { attachments } = defineProps<{
  attachments: WidgetUploadedAttachment[]
}>()

const layoutType = computed(() => {
  if (attachments.length > 1) {
    return 'one-line'
  }
  const mime = attachments[0].mimeType
  return isImageMime(mime) || isVideoMime(mime) ? 'preview' : 'regular'
})

const isPreviewMetadata = (metadata?: MessengerApiAttachmentMetadata) => {
  return isMessengerApiAttachmentImageMetadata(metadata) || isMessengerApiAttachmentVideoMetadata(metadata)
}
</script>

<style lang="scss" scoped>
.messenger-chat-message-attachments {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
